<template>
    <div class="side-mood-hm-stats">
        <div class="smh-header">
            <h2 class="smh-title">{{ $t('sideMoodHmStats.title') }}</h2>
            <div class="smh-tabs-group">
                <span class="smh-tabs-prefix">{{ $t('sideMoodHmStats.performanceBy') }}</span>
                <div class="smh-tabs" role="tablist">
                    <button
                        v-for="tab in tabs"
                        :key="tab.id"
                        type="button"
                        role="tab"
                        class="smh-tab"
                        :class="{ 'smh-tab--active': activeTab === tab.id }"
                        :aria-selected="activeTab === tab.id"
                        @click="activeTab = tab.id"
                    >
                        <v-icon small class="mr-2">fa-regular {{ tab.icon }}</v-icon>
                        {{ $t(tab.labelKey) }}
                    </button>
                </div>
            </div>
        </div>

        <div class="smh-body">
            <aside class="smh-side-panel">
                <h3 class="smh-panel-title">{{ $t('sideMoodHmStats.side.title') }}</h3>
                <p class="smh-panel-subtitle">{{ $t('sideMoodHmStats.side.subtitle') }}</p>

                <div class="smh-side-metric">
                    <v-icon class="smh-side-icon smh-side-icon--buy" small>fa-chart-line</v-icon>
                    <div class="smh-side-metric-content">
                        <div class="smh-side-value">{{ formatSideAmount(buyNetProfit) }}</div>
                        <div class="smh-side-label">{{ getSideLabel('Buy', buyNetProfit) }}</div>
                    </div>
                </div>

                <div class="smh-side-metric">
                    <v-icon class="smh-side-icon smh-side-icon--sell" small>fa-chart-line</v-icon>
                    <div class="smh-side-metric-content">
                        <div class="smh-side-value">{{ formatSideAmount(sellNetProfit) }}</div>
                        <div class="smh-side-label">{{ getSideLabel('Sell', sellNetProfit) }}</div>
                    </div>
                </div>
            </aside>

            <div class="smh-main-panel">
                <div v-show="activeTab === 'mood'" class="smh-tab-panel">
                    <h3 class="smh-panel-title">{{ $t('sideMoodHmStats.mood.title') }}</h3>
                    <p class="smh-panel-subtitle">{{ $t('sideMoodHmStats.mood.subtitle') }}</p>
                    <apexchart
                        type="bar"
                        height="360"
                        :options="moodChartOptions"
                        :series="moodChartSeries"
                    />
                </div>

                <div v-show="activeTab === 'hitsAndMistakes'" class="smh-tab-panel">
                    <h3 class="smh-panel-title">{{ $t('sideMoodHmStats.hitsAndMistakes.title') }}</h3>
                    <p class="smh-panel-subtitle">{{ $t('sideMoodHmStats.hitsAndMistakes.subtitle') }}</p>
                    <apexchart
                        v-if="activeTab === 'hitsAndMistakes'"
                        type="bar"
                        height="360"
                        :options="hmChartOptions"
                        :series="hmChartSeries"
                    />
                </div>
            </div>
        </div>

        <JournalInsightBar :insight="insight" />
    </div>
</template>

<script>
import JournalInsightBar from '@/views/Journal/components/JournalInsightBar';

import ConsiderationRepository from '@/repositories/consideration';

const POSITIVE_COLOR = '#005931';
const NEGATIVE_COLOR = '#D00808';

function createEmptyPerformanceChartOptions() {
    return {
        chart: { type: 'bar', toolbar: { show: false } },
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '55%',
            },
        },
        grid: {
            borderColor: '#f3f4f6',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        xaxis: {
            categories: [],
            labels: {
                show: true,
                style: {
                    colors: '#ababab',
                    fontSize: '12px',
                    fontFamily: 'Avenir, sans-serif',
                },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: -1,
            max: 1,
            tickAmount: 4,
            labels: {
                show: true,
                style: {
                    colors: '#ababab',
                    fontSize: '12px',
                    fontFamily: 'Avenir, sans-serif',
                },
            },
        },
        tooltip: { enabled: true },
    };
}

function aggregateNetProfit(trades, getKey) {
    const map = {};

    trades.forEach((trade) => {
        const key = getKey(trade);
        if (!key) {
            return;
        }
        if (!(key in map)) {
            map[key] = 0;
        }
        map[key] += trade.netProfit;
    });

    return Object.entries(map)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
}

export default {
    name: 'SideMoodHmStats',
    components: { JournalInsightBar },
    props: {
        // AI insight for this section, already resolved by JournalStats (null = nothing to show).
        insight: { type: Object, default: null },
    },
    data() {
        return {
            activeTab: 'mood',
            tabs: [
                { id: 'mood', labelKey: 'sideMoodHmStats.tabs.mood', icon: 'fa-face-smile' },
                { id: 'hitsAndMistakes', labelKey: 'sideMoodHmStats.tabs.hitsAndMistakes', icon: 'fa-bullseye' },
            ],
            buyNetProfit: 0,
            sellNetProfit: 0,
            moodChartSeries: [{ name: 'Performance', data: [] }],
            moodChartOptions: createEmptyPerformanceChartOptions(),
            hmChartSeries: [{ name: 'Performance', data: [] }],
            hmChartOptions: createEmptyPerformanceChartOptions(),
            hmDataLoaded: false,
            hmLoading: false,
        };
    },
    beforeMount() {
        this.refreshStats();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.hmDataLoaded = false;
            this.refreshStats();
        },
        '$i18n.locale': function () {
            this.refreshStats();
        },
        activeTab(value) {
            if (value === 'hitsAndMistakes' && !this.hmDataLoaded && !this.hmLoading) {
                this.buildHitsMistakesChart();
            }
        },
    },
    methods: {
        async refreshStats() {
            this.buildSideStats();
            this.buildMoodChart();
            if (this.activeTab === 'hitsAndMistakes') {
                await this.buildHitsMistakesChart();
            }
        },
        buildSideStats() {
            let buyTotal = 0;
            let sellTotal = 0;

            this.$store.state.book.trades.forEach((trade) => {
                if (trade.side === 'Buy') {
                    buyTotal += trade.netProfit;
                } else if (trade.side === 'Sell') {
                    sellTotal += trade.netProfit;
                }
            });

            this.buyNetProfit = buyTotal;
            this.sellNetProfit = sellTotal;
        },
        buildMoodChart() {
            const trades = this.$store.state.book.trades.slice();
            const entries = aggregateNetProfit(trades, (trade) => {
                if (!trade.mood) {
                    return this.$t('sideMoodHmStats.none');
                }
                return trade.mood;
            });

            this.applyPerformanceChart(entries, 'moodChartSeries', 'moodChartOptions', { invertColors: false });
        },
        async buildHitsMistakesChart() {
            const trades = this.$store.state.book.trades.slice();
            if (!trades.length) {
                this.applyPerformanceChart([], 'hmChartSeries', 'hmChartOptions', { invertColors: true });
                this.hmDataLoaded = true;
                return;
            }

            this.hmLoading = true;
            const considerationMap = {};
            const considerationLookup = {};
            (this.$store.state.user.considerations || []).forEach((item) => {
                considerationLookup[item.id] = item;
            });

            try {
                const responses = await Promise.all(
                    trades.map((trade) =>
                        ConsiderationRepository.getConsiderationsPerTrade(trade.id)
                            .then((response) => ({ trade, considerations: response.data || [] }))
                            .catch(() => ({ trade, considerations: [] }))
                    )
                );

                responses.forEach(({ trade, considerations }) => {
                    considerations.forEach((considerationTrade) => {
                        const consideration = considerationTrade.consideration || considerationLookup[considerationTrade.id];
                        if (!consideration || !consideration.name) {
                            return;
                        }
                        if (consideration.type !== 'HIT' && consideration.type !== 'MISTAKE') {
                            return;
                        }
                        const key = consideration.name;
                        if (!(key in considerationMap)) {
                            considerationMap[key] = 0;
                        }
                        considerationMap[key] += trade.netProfit;
                    });
                });

                const entries = Object.entries(considerationMap)
                    .map(([label, value]) => ({ label, value }))
                    .sort((a, b) => b.value - a.value);

                this.applyPerformanceChart(entries, 'hmChartSeries', 'hmChartOptions', { invertColors: true });
                this.hmDataLoaded = true;
            } finally {
                this.hmLoading = false;
            }
        },
        applyPerformanceChart(entries, seriesKey, optionsKey, { invertColors = false } = {}) {
            const values = entries.map((entry) => entry.value);
            const categories = entries.map((entry) => entry.label);
            const maxAbs = values.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
            const limit = Math.max(maxAbs * 1.15, 1);
            const positiveColor = invertColors ? NEGATIVE_COLOR : POSITIVE_COLOR;
            const negativeColor = invertColors ? POSITIVE_COLOR : NEGATIVE_COLOR;

            this[seriesKey] = [{ name: 'Performance', data: values }];
            this[optionsKey] = {
                theme: {
                    mode: this.$store.state.currentTheme == 'dark' ? 'dark' : 'light',
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    },
                },
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        borderRadius: 4,
                        columnWidth: '55%',
                        colors: {
                            ranges: [
                                { from: -limit, to: -0.0001, color: negativeColor },
                                { from: 0, to: limit, color: positiveColor },
                            ],
                        },
                    },
                },
                dataLabels: { enabled: false },
                legend: { show: false },
                grid: {
                    borderColor: '#f3f4f6',
                    strokeDashArray: 4,
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } },
                },
                annotations: {
                    yaxis: [{
                        y: 0,
                        borderColor: '#d1d5db',
                        strokeDashArray: 0,
                    }],
                },
                xaxis: {
                    categories,
                    labels: {
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    min: -limit,
                    max: limit,
                    tickAmount: 4,
                    labels: {
                        formatter: (value) => this.formatAxisCurrency(value),
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val) => this.formatSignedCurrency(val),
                    },
                },
            };
        },
        formatSideAmount(value) {
            const abs = Math.abs(value);
            if (abs >= 1000) {
                return `${(abs / 1000).toFixed(1)}k`;
            }
            return abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
        },
        formatSignedCurrency(value) {
            const abs = Math.abs(value);
            const formatted = abs >= 1000
                ? `${(abs / 1000).toFixed(1)}k`
                : abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
            if (value > 0) {
                return `+$${formatted}`;
            }
            if (value < 0) {
                return `-$${formatted}`;
            }
            return '$0';
        },
        formatAxisCurrency(value) {
            if (Math.abs(value) < 0.01) {
                return '$0';
            }
            const abs = Math.abs(value);
            const formatted = abs >= 1000
                ? `${(abs / 1000).toFixed(1)}k`
                : abs.toFixed(0);
            return value >= 0 ? `$${formatted}` : `-$${formatted}`;
        },
        getSideLabel(side, value) {
            const isBuy = side === 'Buy';
            if (value >= 0) {
                return isBuy
                    ? this.$t('sideMoodHmStats.side.earnByBuying')
                    : this.$t('sideMoodHmStats.side.earnBySelling');
            }
            return isBuy
                ? this.$t('sideMoodHmStats.side.lostByBuying')
                : this.$t('sideMoodHmStats.side.lostBySelling');
        },
        dayKey(dayIndex) {
            const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            return keys[dayIndex];
        },    },
};
</script>

<style lang="scss" scoped>
.side-mood-hm-stats {
    .smh-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
        margin-bottom: 24px;
    }

    .smh-title {
        margin: 0;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 28px;
        color: var(--v-darkGrey-base);
    }

    .smh-tabs-group {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .smh-tabs-prefix {
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        color: #ababab;
        white-space: nowrap;
    }

    .smh-tabs {
        display: inline-flex;
        align-items: center;
        background: var(--v-lightGray-base);
        border-radius: 8px;
        padding: 4px;
    }

    .smh-tab {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 20px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--v-darkGray-base);
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;
        white-space: nowrap;

        .v-icon {
            color: inherit !important;
        }

        &--active {
            background: var(--v-mainGreen-base);
            color: #F8F8F8;
        }
    }

    .smh-body {
        display: flex;
        gap: 32px;
        align-items: stretch;
    }

    .smh-side-panel {
        flex: 0 0 220px;
        max-width: 220px;
        padding-right: 24px;
        border-right: 1px solid #e5e7eb;
    }

    .smh-main-panel {
        flex: 1;
        min-width: 0;
    }

    .smh-panel-title {
        margin: 0 0 4px;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 16px;
        color: var(--v-mainBlack-base);
    }

    .smh-panel-subtitle {
        margin: 0 0 20px;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        color: #ababab;
    }

    .smh-side-metric {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 28px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .smh-side-icon {
        flex-shrink: 0;
        margin-top: 6px;

        &--buy {
            color: #005931 !important;
        }

        &--sell {
            color: #D00808 !important;
        }
    }

    .smh-side-metric-content {
        min-width: 0;
    }

    .smh-side-value {
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 28px;
        line-height: 1.2;
        margin-bottom: 4px;
        color: var(--v-mainBlack-base);
    }

    .smh-side-label {
        font-family: 'Avenir', sans-serif;
        font-size: 13px;
        color: #ababab;
    }


    @media (max-width: 959px) {
        .smh-body {
            flex-direction: column;
        }

        .smh-side-panel {
            flex: none;
            max-width: none;
            padding-right: 0;
            padding-bottom: 24px;
            border-right: none;
            border-bottom: 1px solid #e5e7eb;
        }
    }
}
</style>
